<script setup lang="ts">
  import { cn } from "@/lib/utils"
  import {
    DrawerContent,
    DrawerHandle,
    DrawerOverlay,
    DrawerPortal,
    injectDrawerRootContext,
    Presence,
    useForwardPropsEmits,
    type DrawerContentEmits,
    type DrawerContentProps,
  } from "reka-ui"
  import { computed, inject, watchEffect, type HTMLAttributes } from "vue"
  import { IconButton } from "@/components/ui/icon-button"

  import {
    SheetClose,
    sheetHandleVariants,
    sheetSwipeDirectionKey,
    sheetVariants,
    swipeDirectionBySide,
    type SheetVariants,
  } from "."

  interface SheetContentProps extends DrawerContentProps {
    class?: HTMLAttributes["class"]
    side?: SheetVariants["side"]
    size?: SheetVariants["size"]
    hideCloseButton?: boolean
    hideOverlay?: boolean
    dismissible?: boolean
  }

  defineOptions({
    inheritAttrs: false,
  })

  const props = withDefaults(defineProps<SheetContentProps>(), {
    hideOverlay: false,
    dismissible: true,
  })

  const emits = defineEmits<DrawerContentEmits>()

  const delegatedProps = computed(() => {
    const {
      class: _,
      side: __,
      size: ___,
      hideCloseButton: ____,
      hideOverlay: _____,
      dismissible: ______,
      ...delegated
    } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const rootContext = injectDrawerRootContext()

  const swipeDirection = inject(sheetSwipeDirectionKey)
  watchEffect(() => {
    if (swipeDirection) {
      swipeDirection.value = swipeDirectionBySide[props.side ?? "right"]
    }
  })

  const blockSwipe = (event: Event) => event.stopImmediatePropagation()
  const swipeBlockers = computed(() =>
    props.dismissible
      ? {}
      : { pointerdown: blockSwipe, touchstart: blockSwipe },
  )
</script>

<template>
  <DrawerPortal>
    <DrawerOverlay
      v-if="!hideOverlay"
      class="fixed inset-0 z-[70] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <!-- Unmount after closing, as reka's DialogContent does, so DrawerContent undoes its aria-hidden once -->
    <Presence :present="props.forceMount || rootContext.open.value">
      <DrawerContent
        :class="cn(sheetVariants({ side, size }), props.class)"
        v-bind="{ ...forwarded, ...$attrs }"
        force-mount
        v-on="swipeBlockers"
      >
        <DrawerHandle
          v-if="dismissible"
          :class="sheetHandleVariants({ side })"
          data-testid="sheet-drag-handle"
        />
        <slot />

        <SheetClose v-if="!hideCloseButton" as-child>
          <IconButton
            variant="text"
            icon="Close"
            label="Close sheet"
            size="md"
            :class="
              cn(
                'absolute right-0 top-0 p-5 pr-7 pt-7 text-current hover:bg-transparent active:bg-transparent dark:hover:bg-transparent dark:active:bg-transparent',
              )
            "
            data-testid="sheet-close-btn"
          >
          </IconButton>
        </SheetClose>
      </DrawerContent>
    </Presence>
  </DrawerPortal>
</template>
